import { useState, useEffect, useRef } from "react";
import { Eye, ChevronDown, ChevronUp, FileText, Printer, Download, AlertTriangle } from "lucide-react";
import api, { API_URL } from "../lib/api";

/**
 * Reusable collapsible PDF preview.
 *
 * Why the blob-URL dance:
 *   Loading the PDF directly via <iframe src="/api/files/..."> works in
 *   standalone mode but fails when the app is double-iframed inside the
 *   BlackScott Hub. Edge proxies + the browser's strict frame policy can
 *   strip our `Content-Security-Policy: frame-ancestors` header and block
 *   the nested PDF render.
 *
 *   Fetching the PDF via axios (with Bearer + cookie auth) into a Blob and
 *   then rendering a `blob:` URL sidesteps the entire frame-embedding
 *   policy chain — the iframe src is now same-origin to the parent
 *   document by construction, no matter how many parent frames there are.
 *
 * Props:
 * - pdfUrl: Backblaze/storage path passed to /api/files/
 * - title: Header text shown when collapsed (e.g. "Original Skyworth Job Card")
 * - downloadFilename: Saved name when user downloads
 * - defaultOpen: Start expanded (default false)
 * - testIdPrefix: prefix for data-testid attributes (default "original-jc")
 * - height: iframe height in CSS px (default 520)
 */
export default function CollapsiblePdfPreview({
  pdfUrl,
  title = "Original Skyworth Job Card",
  downloadFilename = "JobCard.pdf",
  defaultOpen = false,
  testIdPrefix = "original-jc",
  height = 520,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const blobUrlRef = useRef(null);

  // Direct streaming URL — used by Print + Download buttons (those open a
  // brand-new top-level window so iframe restrictions don't apply).
  const directUrl = `${API_URL}/api/files/${pdfUrl}`;

  // Fetch the PDF as a Blob the first time the preview is opened.
  useEffect(() => {
    if (!open || !pdfUrl || blobUrl) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/files/${pdfUrl}`, { responseType: "blob" })
      .then((resp) => {
        if (cancelled) return;
        const url = URL.createObjectURL(resp.data);
        blobUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.detail || "Failed to load PDF");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, pdfUrl, blobUrl]);

  // Revoke the blob URL on unmount so we don't leak memory.
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  if (!pdfUrl) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-slate-200 bg-slate-50">
        <button
          type="button"
          data-testid={`${testIdPrefix}-toggle`}
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-slate-700 hover:text-teal-600"
        >
          <Eye size={13} className="text-teal-600" />
          <span className="text-[11px] uppercase tracking-wide font-semibold">{title}</span>
          {!open && <span className="text-[10px] text-slate-400">(click to view)</span>}
          {open
            ? <ChevronUp size={14} className="text-slate-500" />
            : <ChevronDown size={14} className="text-slate-500" />}
        </button>
        <div className="flex items-center gap-2">
          <a
            data-testid={`${testIdPrefix}-print`}
            href={directUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 text-xs font-medium rounded-lg transition-colors"
            title="Open in new tab to print"
          >
            <Printer size={12} /> Print
          </a>
          <a
            data-testid={`${testIdPrefix}-download`}
            href={directUrl}
            download={downloadFilename}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium rounded-lg transition-colors"
          >
            <Download size={12} /> Download PDF
          </a>
        </div>
      </div>
      {open && (
        <div className="bg-white">
          {loading && (
            <div className="flex items-center justify-center text-slate-500 text-sm" style={{ height: `${height}px` }}>
              Loading PDF…
            </div>
          )}
          {error && !loading && (
            <div className="flex flex-col items-center justify-center text-center text-slate-600 text-sm gap-2 px-6" style={{ height: `${height}px` }}>
              <AlertTriangle className="text-amber-500" size={28} />
              <p className="font-medium">PDF preview failed to load</p>
              <p className="text-xs text-slate-400">{error}</p>
              <a
                href={directUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium rounded-lg"
              >
                Open in new tab
              </a>
            </div>
          )}
          {!loading && !error && blobUrl && (
            <iframe
              data-testid={`${testIdPrefix}-iframe`}
              src={`${blobUrl}#view=FitH`}
              title={title}
              className="w-full"
              style={{ height: `${height}px` }}
            />
          )}
          <div className="px-3 py-1.5 text-right border-t border-slate-100 bg-slate-50">
            <button
              type="button"
              data-testid={`${testIdPrefix}-minimize`}
              onClick={() => setOpen(false)}
              className="text-[10px] text-slate-500 hover:text-teal-600 inline-flex items-center gap-1"
            >
              <ChevronUp size={11} /> Minimize
            </button>
          </div>
        </div>
      )}
      {!open && (
        <div className="px-3 py-5 bg-slate-50/50 flex items-center justify-center text-slate-400 text-xs">
          <FileText size={14} className="mr-1.5" /> Preview hidden — click the header above to view
        </div>
      )}
    </div>
  );
}
